import { z } from 'zod';
import { normalizeUsername, passwordError, usernameError } from '../auth';
import { role, uuid } from './common';

/** Prototype toast texts (saveAuthUser line 3125, authLogin line 3101, openUsersModal line 3113). */
export const NAME_REQUIRED_MESSAGE = 'Shkruaj emrin';
export const LOGIN_FAILED_MESSAGE = 'Username ose fjalëkalimi nuk është i saktë';
export const USERNAME_TAKEN_MESSAGE = 'Ky username ekziston';
export const USERS_ADMIN_ONLY_MESSAGE = 'Vetëm Drejtori / Administratori mund t’i menaxhojë hyrjet';
/** Same text `passwordError('')` returns (auth.ts); used when a new login is created without a password. */
export const DELIVERY_NEEDS_EMPLOYEE_MESSAGE = 'Zgjedh punëtorin që do t’i marrë porositë me këtë hyrje';
export const PASSWORD_REQUIRED_MESSAGE = 'Fjalëkalimi duhet t’i ketë së paku 4 karaktere';

const fullName = z.string().trim().min(1, { message: NAME_REQUIRED_MESSAGE });

const username = z
  .string()
  .transform(normalizeUsername)
  .superRefine((u, ctx) => {
    const error = usernameError(u);
    if (error) ctx.addIssue({ code: z.ZodIssueCode.custom, message: error });
  });

const password = z.string().superRefine((p, ctx) => {
  const error = passwordError(p);
  if (error) ctx.addIssue({ code: z.ZodIssueCode.custom, message: error });
});

/** Empty means "keep the current password" (prototype saveAuthUser: `if(p){...}`). */
const optionalPassword = z
  .string()
  .optional()
  .superRefine((p, ctx) => {
    if (!p) return;
    const error = passwordError(p);
    if (error) ctx.addIssue({ code: z.ZodIssueCode.custom, message: error });
  });

/** First-run administrator (task.md §4.1); issues come out in the prototype's order name → username → password. */
export const setupAdminForm = z.object({
  name: fullName,
  username,
  password,
});
export type SetupAdminForm = z.infer<typeof setupAdminForm>;

/** Login: the prototype shows one generic error for every failure (line 3101). */
export const loginForm = z.object({
  username: z
    .string()
    .transform(normalizeUsername)
    .pipe(z.string().min(1, { message: LOGIN_FAILED_MESSAGE })),
  password: z.string().min(1, { message: LOGIN_FAILED_MESSAGE }),
});
export type LoginForm = z.infer<typeof loginForm>;

/** Users modal (prototype saveAuthUser) extended with role + employee link for per-employee logins. */
export const userUpsertForm = z
  .object({
    id: uuid.optional(),
    name: fullName,
    username,
    password: optionalPassword,
    role,
    employee_id: uuid.nullable().default(null),
  })
  .superRefine((form, ctx) => {
    if (form.id === undefined && !form.password) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['password'], message: PASSWORD_REQUIRED_MESSAGE });
    }
    // A delivery login exists to receive that worker's orders; unlinked, it would receive nothing.
    if (form.role === 'delivery' && !form.employee_id) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['employee_id'],
        message: DELIVERY_NEEDS_EMPLOYEE_MESSAGE,
      });
    }
  });
export type UserUpsertForm = z.infer<typeof userUpsertForm>;
export type UserUpsertInput = z.input<typeof userUpsertForm>;
